import { isAxiosError } from "axios";

/**
 * The BDC endpoints answer with a Thai `message` already (BDC_* in the backend's response
 * enum); these fallbacks cover a body that cannot be read or carries no message.
 */
const PREVIEW_FALLBACK_BY_STATUS: Record<number, string> = {
  400: "ไม่พบไฟล์ หรือไฟล์มีขนาดใหญ่เกินไป",
  403: "บัญชีนี้ไม่มีสิทธิ์นำเข้าไฟล์ BDC (เมนู BDC ใช้ได้เฉพาะสำนักงานใหญ่และผู้ใช้ของสาขา BDC)",
  415: "ไฟล์ไม่รองรับ กรุณาเลือกไฟล์ .txt ของ ORDS / PICKLST",
  422: "อ่านไฟล์ไม่ได้ หรือยังไม่มีสาขาใดลงทะเบียนเป็นสาขา BDC (ให้ MASTER ลงทะเบียนในหน้าตั้งค่า BDC › สาขา)",
};

/** Confirm failures by status — 422 BDC_IMPORT_DC_UNBOUND / BLOCKED, 502 BDC_IMPORT_FILE_STORE_FAILED. */
const CONFIRM_FALLBACK_BY_STATUS: Record<number, string> = {
  403: "ผลตรวจไฟล์นี้เป็นของผู้ใช้อื่น หรือบัญชีนี้ไม่มีสิทธิ์นำเข้าไฟล์ BDC",
  404: "ผลตรวจหมดอายุหรือถูกใช้ไปแล้ว กรุณาเลือกไฟล์แล้วตรวจใหม่",
  422: "มี DC ที่ยังไม่ได้ผูกกับสาขา BDC ให้หัวหน้า BDC ผูก DC ในหน้า ตั้งค่า BDC ก่อน แล้วอัปโหลดไฟล์ใหม่",
  502: "เก็บไฟล์ต้นฉบับ BDC ไม่สำเร็จ ยังไม่ได้บันทึกข้อมูล กรุณากดยืนยันอีกครั้ง",
};

const textOf = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

const serverMessage = (error: unknown): { status: number | null; message: string | null } => {
  if (!isAxiosError(error) || !error.response) return { status: null, message: null };
  const body = error.response.data as { message?: unknown; error?: unknown } | undefined;
  return { status: error.response.status, message: textOf(body?.message) ?? textOf(body?.error) };
};

/** The preview call owns its error (no global toast): show the server's Thai reason by the picker. */
export const resolveBdcPreviewFailure = (error: unknown): string => {
  if (!isAxiosError(error)) return "ตรวจไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
  if (!error.response) return "ติดต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองใหม่";
  const { status, message } = serverMessage(error);
  return (
    message ??
    (status !== null ? PREVIEW_FALLBACK_BY_STATUS[status] : undefined) ??
    `ตรวจไฟล์ไม่สำเร็จ (Error ${status})`
  );
};

/**
 * What the screen does after a failed confirm:
 *   expired   — 404: the stashed check is gone → pick the files and check again
 *   bind_dc   — 422: a DC is unbound (or the check was blocked) → link to ตั้งค่า BDC › DC
 *   retry     — 502: the originals did not reach storage, nothing was saved → press confirm again
 *   other     — anything else; the message says why
 */
export type BdcConfirmFailureKind = "expired" | "bind_dc" | "retry" | "other";

export interface BdcConfirmFailure {
  kind: BdcConfirmFailureKind;
  message: string;
}

export const resolveBdcConfirmFailure = (error: unknown): BdcConfirmFailure => {
  if (!isAxiosError(error)) return { kind: "other", message: "ยืนยันนำเข้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง" };
  if (!error.response) {
    return { kind: "retry", message: "ติดต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วกดยืนยันอีกครั้ง" };
  }
  const { status, message } = serverMessage(error);
  const kind: BdcConfirmFailureKind =
    status === 404 ? "expired" : status === 422 ? "bind_dc" : status === 502 ? "retry" : "other";
  return {
    kind,
    message:
      message ??
      (status !== null ? CONFIRM_FALLBACK_BY_STATUS[status] : undefined) ??
      `ยืนยันนำเข้าไม่สำเร็จ (Error ${status})`,
  };
};
